// @ts-nocheck
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { CONSTRAINT_TYPE_LABELS } from "@/lib/constraints/humanize";

const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;

interface Props {
  eventId: string;
  /** teamId (RecruitLook id) -> display name */
  teamNameLookup?: (teamId: string) => string | undefined;
}

export function RefundRecommendationsPanel({ eventId, teamNameLookup }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [dismissTarget, setDismissTarget] = useState<any>(null);
  const [dismissNotes, setDismissNotes] = useState("");

  const { data: recs = [] } = useQuery({
    queryKey: ["refund-recommendations", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("schedule_refund_recommendations")
        .select("*")
        .eq("event_id", eventId)
        .eq("status", "pending_review")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["refund-recommendations", eventId] });
    queryClient.invalidateQueries({ queryKey: ["scheduling-requests", eventId] });
  };

  const rows = useMemo(() => recs, [recs]);
  if (rows.length === 0) return null;

  const handleApprove = async (rec: any) => {
    const raw = amounts[rec.id];
    const amount = raw === undefined || raw === "" ? Number(rec.recommended_amount) : Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a refund amount greater than zero.");
      return;
    }
    if (amount > Number(rec.recommended_amount)) {
      toast.error("Refund cannot exceed the fee that was charged.");
      return;
    }
    setBusyId(rec.id);
    try {
      // The edge function only refunds once the recommendation is approved.
      const { error: upErr } = await (supabase as any)
        .from("schedule_refund_recommendations")
        .update({ status: "approved", recommended_amount: amount })
        .eq("id", rec.id)
        .eq("status", "pending_review");
      if (upErr) throw upErr;

      const { data, error } = await invokeEdgeFunction("refund-constraint-fee", {
        body: { constraint_id: rec.constraint_id, amount },
      });
      if (error || data?.error) {
        await (supabase as any)
          .from("schedule_refund_recommendations")
          .update({ status: "pending_review" })
          .eq("id", rec.id)
          .eq("status", "approved");
        toast.error(`Refund failed: ${error?.message || data?.error}`);
        return;
      }

      await (supabase as any)
        .from("schedule_refund_recommendations")
        .update({
          status: "refunded",
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", rec.id);
      toast.success(`Refunded ${money(amount)}`);
      refresh();
    } catch (e: any) {
      toast.error(e?.message || "Refund failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async () => {
    if (!dismissTarget) return;
    const notes = dismissNotes.trim();
    if (!notes) {
      toast.error("A note is required to dismiss.");
      return;
    }
    setBusyId(dismissTarget.id);
    try {
      const { error } = await (supabase as any)
        .from("schedule_refund_recommendations")
        .update({
          status: "dismissed",
          review_notes: notes,
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", dismissTarget.id)
        .eq("status", "pending_review");
      if (error) throw error;
      toast.success("Recommendation dismissed");
      setDismissTarget(null);
      setDismissNotes("");
      refresh();
    } catch (e: any) {
      toast.error(e?.message || "Could not dismiss");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Card className="border-amber-300">
        <CardHeader>
          <CardTitle className="text-base">
            Refunds to review ({rows.length})
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            These teams paid for requests the published placement could not honor. Nothing is
            refunded until you approve it.
          </p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team</TableHead>
                <TableHead>Request</TableHead>
                <TableHead>Why it wasn't honored</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Refund</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((rec: any) => (
                <TableRow key={rec.id}>
                  <TableCell className="font-medium">
                    {teamNameLookup?.(String(rec.team_id ?? "")) || "—"}
                  </TableCell>
                  <TableCell>
                    {CONSTRAINT_TYPE_LABELS[rec.constraint_type] || rec.constraint_type || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-md">
                    {rec.reason || "—"}
                  </TableCell>
                  <TableCell>{money(rec.total_amount_paid)}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-24"
                      value={amounts[rec.id] ?? String(Number(rec.recommended_amount || 0))}
                      onChange={(e) =>
                        setAmounts((a) => ({ ...a, [rec.id]: e.target.value }))
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    <Button
                      size="sm"
                      disabled={busyId === rec.id}
                      onClick={() => handleApprove(rec)}
                    >
                      Approve refund
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === rec.id}
                      onClick={() => {
                        setDismissTarget(rec);
                        setDismissNotes("");
                      }}
                    >
                      Dismiss
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog
        open={!!dismissTarget}
        onOpenChange={(o) => {
          if (!o) { setDismissTarget(null); setDismissNotes(""); }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss this refund recommendation</AlertDialogTitle>
            <AlertDialogDescription>
              No money moves. Say why, so the decision is on record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={dismissNotes}
            onChange={(e) => setDismissNotes(e.target.value)}
            placeholder="Why is no refund owed?"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDismiss(); }}
              disabled={!dismissNotes.trim() || busyId === dismissTarget?.id}
            >
              Dismiss
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
